import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ConstraintTypeEnum, IConstraintMatchInfo, SeConstraintNotifierService } from '@se/constraint';
import { BindingType, IBindingRule } from '@se/cp.common';
import { DataBinding, Logger } from '@se/se.ui.html5';
import { isEmpty } from 'lodash-es';
import { BehaviorSubject, Observable, Subject, Subscription, asyncScheduler } from 'rxjs';
import { debounceTime, delay, subscribeOn, takeUntil } from 'rxjs/operators';

import { CpControlType, ICpEntity } from '../cp-configuration/view-model';
import { CpControlErrorStateMatcher } from './error-state-matcher';
import { SeConstraintValidator } from './se-constraint-validator';

export interface CpControlContainerInfo {
    numColumns: number;
}

@Directive()
export abstract class AbstractControlComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
    @Input() cardId!: string;

    /**
     * Il CpControl che deve essere renderizzato.
     */
    @Input() control!: CpControlType;

    /**
     * Il ReactiveForm padre.
     */
    @Input() form!: FormGroup;

    @Input()
    set disabled(value: boolean) {
        this.isDisabledByBinding = value;
    }
    /**
     * Se forzare la disabilitazione del controllo.
     */
    @Input()
    set isDisabledByBinding(value: boolean) {
        this._isDisabledByBinding = value;

        if (this.isInitialized) {
            this.updateFormControl();
        }
    }

    @Input() containerInfo!: CpControlContainerInfo;

    /**
     * Emette un evento quando il controllo riceve focus.
     */
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() focusin = new EventEmitter<void>();

    /**
     * Emette un evento quando il modello cambia per azione dell'utente.
     */
    @Output() edited = new EventEmitter<void>();

    /**
     * Emette un evento quando il controllo ha un warning.
     */
    @Output() warningChanged = new EventEmitter<string | null>();
    private _warningMessage: string | null = '';
    get warningMessage() {
        return this._warningMessage;
    }
    set warningMessage(message: string | null) {
        this._warningMessage = message;
        this.warningChanged.emit(this._warningMessage);
    }

    /**
     * Emette un evento quando il modello cambia per azione di una regola.
     */
    @Output() autoUpdated = new EventEmitter<boolean>();
    private _isAutoUpdated = false;
    get isAutoUpdated() {
        return this._isAutoUpdated;
    }
    set isAutoUpdated(autoUpdated: boolean) {
        this._isAutoUpdated = autoUpdated;
        this.autoUpdated.emit(autoUpdated);
    }

    /**
     * Emette un evento quando il controllo ha dati provenienti da AI .
     */
    @Output() hasAIDataChanged = new EventEmitter<boolean>();
    private _hasAIData = false;
    get hasAIData() {
        return this._hasAIData;
    }
    set hasAIData(aiData: boolean) {
        this._hasAIData = aiData;
        this.hasAIDataChanged.emit(aiData);
    }

    /**
     * Per forzare il mostrare gli errori anche quando il controllo non è 'touched'.
     */
    errorStateMatcher = new CpControlErrorStateMatcher();

    /**
     * Il FormControl relativo al controllo. Possibilmente nullo.
     */
    formControl?: AbstractControl;

    get isHidden() {
        return this._isHidden;
    }

    /**
     * Se il controllo è nascosto. Forza la change detection manualmente.
     */
    set isHidden(value: boolean) {
        this._isHidden = value;

        this.cdr.detectChanges();
    }

    /**
     * Consideriamo un controllo come disabilitato se è disabilitato dal padre o se c'è un constraint di tipo 'Enabled' invalido.
     */
    get isDisabled() {
        if ('readonly' in this.control && this.control.readonly === true) {
            return true;
        }

        return this._isDisabledByBinding || this._isDisabledByConstraint;
    }

    /**
     * Invece che mantenere una lista di Subscription, emettiamo un evento durante l'ngOnDestroy.
     * Questo vuol dire che le singole subscription devono essere "takeUntil(this.destroyed$)".
     */
    protected destroyed$ = new Subject<void>();

    /**
     * La entity a cui fa riferimento il controllo. Possibilmente nulla.
     * C'è una possibilità di refactoring per cui sia il CpControl ad avere le logiche necessarie al binding al modello e la validazione.
     */
    protected _entitySubject = new BehaviorSubject<ICpEntity | undefined>(undefined);

    protected get entity(): ICpEntity | undefined {
        return this._entitySubject.value;
    }

    /**
     * Teniamo traccia dell'inizializzazione del controllo per evitare qualche ciclo di change detection.
     */
    private isInitialized = false;

    /**
     * Le Subscription dei validatori associati a questo controllo.
     */
    private validatorSubscriptions: Subscription[] = [];

    /**
     * Backing properties dei getter e setter sopra.
     */
    private _isDisabledByBinding = false;
    private _isDisabledByConstraint = false;
    private _isHidden = false;

    constructor(
        protected cdr: ChangeDetectorRef,
        protected constraintNotifier: SeConstraintNotifierService,
        protected logger?: Logger,
    ) {}

    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
    ngAfterViewInit() {
        // Da chiamare nella superclasse.
    }

    /**
     * Non chiamato normalmente quando il controllo è istanziato dinamicamente.
     */
    ngOnChanges(changes: SimpleChanges) {
        if (changes.control && !changes.control.isFirstChange()) {
            return this.logger && this.logger.warn('Changing CpControl after construction is not supported.');
        }
    }

    ngOnInit(): void {
        if (!this.control) {
            return this.logger && this.logger.warn('CpControl initialized with null control binding.');
        }

        // Delega l'inizializzazione del controllo implementante.
        this.init();

        this.initFormControl();

        if (!isEmpty(this.control.constraints)) {
            this.initConstraints();
        }

        this.updateFormControl();

        this.isInitialized = true;
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this._entitySubject.complete();
        this.validatorSubscriptions.forEach((s) => s.unsubscribe());
        this.form?.removeControl(this.control.id);
    }

    protected abstract init(): void;

    protected abstract onModelChange(value: unknown): boolean;

    protected makeValidator(
        entityObservable: Observable<ICpEntity | undefined>,
        propertyPath: string,
        required: boolean,
    ) {
        const { subscription, validator } = SeConstraintValidator(entityObservable, propertyPath, required);

        this.validatorSubscriptions.push(subscription);

        return validator;
    }

    private handleNotificationConstraint(action: BindingType, matchInfo: IConstraintMatchInfo) {
        switch (action) {
            case BindingType.Calculation:
                if (this.formControl && matchInfo.matchingResult !== this.formControl.value) {
                    if (this.onModelChange(matchInfo.matchingResult)) {
                        this.edited.emit();
                    }
                }
                break;
            case BindingType.Enabled:
                this._isDisabledByConstraint = !matchInfo.matchingResult;
                this.handleDisabledNotification(!matchInfo.matchingResult);
                break;
            case BindingType.Visibility:
                this.isHidden = !matchInfo.matchingResult;
                this.handleHiddenNotification(!matchInfo.matchingResult);
                break;
            case BindingType.Tooltip:
                this.handleTooltipNotification(matchInfo.matchingResult);
                break;
            case BindingType.VisibilityOrEmpty:
                this.isHidden = !matchInfo.matchingResult;
                if (this.isHidden) this.empty();
                this.handleVisibilityOrEmptyNotification(matchInfo.matchingResult);
        }
    }

    private initConstraints() {
        const dataBindingContext =
            'dataBinding' in this.control && this.control.dataBinding ? this.control.dataBinding.entity : undefined;

        this.control.constraints
            .filter(
                (c) =>
                    c.action === BindingType.Calculation ||
                    c.action === BindingType.Enabled ||
                    c.action === BindingType.Tooltip ||
                    c.action === BindingType.Visibility ||
                    c.action === BindingType.VisibilityOrEmpty,
                //lasciamo il supporto per aggiornare un valore di un campo
                //con il risultato di una regola (in futuro forse può essere utile),
                //ma per ora togliamo la possibilità di agggiungere quest'azione in configurazione
                //  c.action === BindingType.Validation
            )
            .forEach((constraint) => {
                this.constraintNotifier
                    .register(constraint.constraintId, dataBindingContext?.toString())
                    .pipe(takeUntil(this.destroyed$), delay(100), subscribeOn(asyncScheduler))
                    .subscribe((matchInfo) => this.handleMatchInfo(constraint, matchInfo));
            });
    }

    private initFormControl() {
        if (this.formControl) {
            // Per ora l'implementante si occupa di fornire il metodo di aggiornamento della entity.
            this.formControl.valueChanges.pipe(takeUntil(this.destroyed$), debounceTime(100)).subscribe((model) => {
                if (this.onModelChange(model) && !this.isDisabled) {
                    this.edited.emit();
                }
            });

            if (!this.form.get(this.control.id)) {
                this.form.addControl(this.control.id, this.formControl);
            }
        }
        if (this.entity) {
            this.entity.autoUpdatedPropertyList$.subscribe((autoUpdatedPropertyList) => {
                const dataBinding: DataBinding | undefined = Reflect.get(this.control, 'dataBinding');
                if (dataBinding) {
                    const propertyName = dataBinding.propertyName;

                    if (!!propertyName) {
                        const isAutoUpdated = !!autoUpdatedPropertyList[propertyName];
                        if (isAutoUpdated !== this.isAutoUpdated) {
                            this.isAutoUpdated = isAutoUpdated;
                        }
                    }
                }
            });
        }

        // Collega i cambiamenti della validazione della entity al FormControl.
        if (
            this.entity &&
            this.entity.propertyErrorStatus &&
            'dataBinding' in this.control &&
            this.control.dataBinding
        ) {
            this.entity
                .propertyErrorStatus(this.control.dataBinding.propertyName)
                .pipe(takeUntil(this.destroyed$))
                .subscribe(() => {
                    this.formControl?.updateValueAndValidity();
                });
        }
    }

    private empty() {
        if (this.onModelChange(null)) {
            this.edited.emit();
        }
    }

    protected handleFilterNotification(_: string) {
        /* Da implementare se necessario nelle classi derivate */
    }

    protected handleTooltipNotification(_: string) {
        /* Da implementare se necessario nelle classi derivate */
    }

    protected handleHiddenNotification(_: boolean) {
        /* Da implementare se necessario nelle classi derivate */
    }

    protected handleDisabledNotification(_: boolean) {
        /* Da implementare se necessario nelle classi derivate */
    }

    protected handleVisibilityOrEmptyNotification(_: boolean) {
        /* Da implementare se necessario nelle classi derivate */
    }

    protected updateFormControl() {
        if (!this.formControl) {
            return;
        }

        if (this.isDisabled !== this.formControl.disabled) {
            this.isDisabled ? this.formControl.disable() : this.formControl.enable();
        }
    }

    private handleMatchInfo(constraint: IBindingRule, matchInfo: IConstraintMatchInfo) {
        const constraintType = matchInfo.constraint.constraintType;
        switch (constraintType) {
            case ConstraintTypeEnum.Filter:
                this.handleFilterNotification(constraint.constraintId);
                break;
            case ConstraintTypeEnum.Notification:
                this.handleNotificationConstraint(constraint.action, matchInfo);
                break;
            case ConstraintTypeEnum.Validation:
                this.formControl?.updateValueAndValidity();
                break;
            default:
                return;
        }

        this.updateFormControl();

        this.cdr.detectChanges();
    }
}
